import React, { useState, useEffect } from 'react';
import { TextField, Checkbox, Radio, RadioGroup, FormControlLabel, FormControl, InputLabel, Select, MenuItem, FormHelperText, Box, Grid } from '@mui/material';

const InputWithFormat = ({ icon, value, data, errorText, label, format, text, isRequired, onChanged }) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [inputValues, setInputValues] = useState([]);
    const [inputData, setInputData] = useState(data || {});

    useEffect(() => {
        setInputValue(value || '');
        setInputData(data || {});
    }, [value, data]);

    const handleInputChange = (newValue) => {
        setInputValue(newValue);
        onChanged([newValue]);
    };

    const handleCheckboxChange = (key, checked) => {
        setInputValues((prev) => {
            const newValues = checked
                ? [...prev, key]
                : prev.filter((val) => val !== key);
            onChanged(newValues);
            return newValues;
        });
    };

    const handleRadioChange = (value) => {
        console.log(value);
        setInputValue(value);
        onChanged([value]);
    };

    const renderInputContainer = (children) => {
        return (
            <Box sx={{ mb: 2, padding: 2, border: errorText ? '1px solid red' : '1px solid transparent', borderRadius: 1 }}>
                <Box sx={{ mb: 1, fontWeight: 'bold' }}>
                    {text}
                    {isRequired && ' *'}
                </Box>
                {children}
                {errorText && <FormHelperText error>{errorText}</FormHelperText>}
            </Box>
        );
    };

    const renderFreeArea = () => {
        return renderInputContainer(
            <TextField
                multiline
                rows={5}
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                variant="outlined"
                fullWidth
            />
        );
    };

    const renderFreeNumber = () => {
        return renderInputContainer(
            <TextField
                type="number"
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                variant="outlined"
                fullWidth
            />
        );
    };

    const renderFreeText = () => {
        return renderInputContainer(
            <TextField
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                variant="outlined"
                fullWidth
            />
        );
    };

    const renderMultipleList = () => {
        return renderInputContainer(
            <Box>
                {Object.entries(inputData).map(([key, label]) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                            checked={inputValues.includes(key)}
                            onChange={(e) => handleCheckboxChange(key, e.target.checked)}
                        />
                        {label}
                    </Box>
                ))}
            </Box>
        );
    };

    const renderMultipleImg = () => {
        return renderInputContainer(
            <Box>
                {Object.entries(inputData).map(([key, imgUrl]) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                            checked={inputValues.includes(key)}
                            onChange={(e) => handleCheckboxChange(key, e.target.checked)}
                        />
                        <Box sx={{ width: 200, height: 200, border: '2px solid purple' }}>
                            <img src={imgUrl} alt={key} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        </Box>
                    </Box>
                ))}
            </Box>
        );
    };

    const renderSingleDropdown = () => {
        return renderInputContainer(
            <FormControl fullWidth variant="outlined">
                <InputLabel>Select Answer</InputLabel>
                <Select
                    value={inputValue}
                    onChange={(e) => handleInputChange(e.target.value)}
                    label="Select Answer"
                >
                    {Object.entries(inputData).map(([key, label]) => (
                        <MenuItem key={key} value={key}>
                            {label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };

    const renderSingleImg = () => {
        return renderInputContainer(
            <Box>
                {Object.entries(inputData).map(([key, imgUrl]) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Radio
                            checked={inputValue === key}
                            onChange={() => handleRadioChange(key)}
                            value={key}
                        />
                        <Box sx={{ width: 200, height: 200, border: '2px solid purple' }}>
                            <img src={imgUrl} alt={key} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        </Box>
                    </Box>
                ))}
            </Box>
        );
    };

    const renderSingleLine = () => {
        return renderInputContainer(
            <RadioGroup value={inputValue} onChange={(e) => handleRadioChange(e.target.value)} row>
                {Object.entries(inputData).map(([key, label]) => (
                    <FormControlLabel key={key} value={key} control={<Radio />} label={label} />
                ))}
            </RadioGroup>
        );
    };

    const renderSingleList = () => {
        return renderInputContainer(
            <RadioGroup value={inputValue} onChange={(e) => handleRadioChange(e.target.value)}>
                {Object.entries(inputData).map(([key, label]) => (
                    <FormControlLabel key={key} value={key} control={<Radio />} label={label} />
                ))}
            </RadioGroup>
        );
    };

    const renderSingleScale = (scale) => {
        const labels = Array.isArray(label) && label.length === 2 ? label : ['', ''];
        return renderInputContainer(
            <Box className="flex-col sm:flex-row items-start sm:items-center" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box mx='5px'>{labels[0]}</Box>
                {[...Array(scale).keys()].map((index) => (
                    <Box ml='5px' key={index}>
                        <FormControlLabel
                            control={
                                <Radio
                                    checked={inputValue[0] === (index + 1).toString()}
                                    onChange={() => handleRadioChange((index + 1).toString())}
                                    value={(index + 1).toString()}
                                />
                            }
                            label={(index + 1).toString()}
                        />
                    </Box>
                ))}
                <Box mx='5px'>{labels[1]}</Box>
            </Box>
        );
    };

    const renderContent = () => {
        const fmt = format.includes('scale') ? 'single-scale' : format;
        switch (fmt) {
            case 'multiple-list':
                return renderMultipleList();
            case 'multiple-img':
                return renderMultipleImg();
            case 'single-dropdown':
                return renderSingleDropdown();
            case 'single-img':
                return renderSingleImg();
            case 'single-line':
                return renderSingleLine();
            case 'single-list':
                return renderSingleList();
            case 'single-scale':
                return renderSingleScale(Number(format.split('-').pop()));
            case 'free-area':
                return renderFreeArea();
            case 'free-number':
                return renderFreeNumber();
            case 'free-text':
                return renderFreeText();
            default:
                return renderInputContainer(null);
        }
    };

    return <>{renderContent()}</>;
};

export default InputWithFormat;
